import React, { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, Mail, Phone, Building2, Tag, Trash2, RefreshCw, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "cetc_admin_pass";
const EXPIRY_KEY = "cetc_admin_expiry";
const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24h

const INTEREST_LABELS = {
  consulting: "IT Consulting",
  training: "Training & Certification",
  managed: "Managed Services",
  staffing: "Recruiting & Staffing",
  general: "General Inquiry",
};

export default function Admin() {
  const [passphrase, setPassphrase] = useState(() => {
    const expiry = Number(localStorage.getItem(EXPIRY_KEY) || 0);
    if (expiry && Date.now() > expiry) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(EXPIRY_KEY);
      return "";
    }
    return localStorage.getItem(STORAGE_KEY) || "";
  });
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const expiry = Number(localStorage.getItem(EXPIRY_KEY) || 0);
    if (stored && expiry && Date.now() <= expiry) {
      tryAuth(stored, true);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(EXPIRY_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryAuth = async (pass, silent = false) => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Passphrase": pass } });
      localStorage.setItem(STORAGE_KEY, pass);
      localStorage.setItem(EXPIRY_KEY, String(Date.now() + SESSION_DURATION_MS));
      setAuthed(true);
      await loadSubmissions(pass);
      if (!silent) toast.success("Welcome back");
    } catch (e) {
      if (e?.response?.status === 401) {
        if (!silent) toast.error("Invalid passphrase");
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(EXPIRY_KEY);
      } else if (e?.response?.status === 429) {
        if (!silent) toast.error("Too many attempts. Try again in a minute.");
      } else if (!silent) {
        toast.error("Something went wrong");
      }
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async (pass) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/contacts`, { headers: { "X-Admin-Passphrase": pass } });
      setSubmissions(res.data);
    } catch (e) {
      toast.error("Could not load submissions");
    } finally {
      setLoading(false);
    }
  };

  const onLogin = (e) => {
    e.preventDefault();
    if (!passphrase) return;
    tryAuth(passphrase);
  };

  const onLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(EXPIRY_KEY);
    setPassphrase("");
    setAuthed(false);
    setSubmissions([]);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    const pass = localStorage.getItem(STORAGE_KEY);
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: { "X-Admin-Passphrase": pass } });
      setSubmissions((s) => s.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = submissions.filter((s) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [s.name, s.email, s.company, s.message, s.interest]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(q));
  });

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-6">
        <form onSubmit={onLogin} className="w-full max-w-md bg-slate-900 border border-slate-800 p-10" data-testid="admin-login-form">
          <div className="h-12 w-12 bg-emerald-500 text-slate-950 flex items-center justify-center">
            <Lock className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <h1 className="mt-8 font-display text-3xl font-semibold tracking-tight">Admin access</h1>
          <p className="mt-3 text-slate-400 text-sm">Enter the passphrase to review contact submissions.</p>
          <label className="block mt-8">
            <span className="block text-xs font-medium text-slate-300 uppercase tracking-widest mb-2">Passphrase</span>
            <input
              type="password"
              data-testid="admin-passphrase-input"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              autoFocus
              className="w-full bg-slate-950 border border-slate-700 px-4 py-3 text-white focus:border-emerald-500 focus:ring-0 focus:outline-none"
            />
          </label>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="mt-6 w-full bg-emerald-500 text-slate-950 py-3 font-medium hover:bg-emerald-400 transition-colors disabled:opacity-60"
          >
            {loading ? "Verifying…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="eyebrow text-emerald-600">Admin</div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900">
              Contact submissions
            </h1>
            <p className="mt-3 text-slate-600">
              {submissions.length} total · {filtered.length} shown
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadSubmissions(localStorage.getItem(STORAGE_KEY))}
              className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium hover:border-slate-900 transition-colors"
              data-testid="admin-refresh"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Refresh
            </button>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium hover:border-slate-900 transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Log out
            </button>
          </div>
        </div>

        <div className="relative mb-6">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
          <input
            type="text"
            data-testid="admin-search"
            placeholder="Search by name, email, company, message…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-white border border-slate-300 pl-11 pr-4 py-3 text-slate-900 focus:border-emerald-500 focus:ring-0 focus:outline-none"
          />
        </div>

        {loading && submissions.length === 0 ? (
          <div className="text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-slate-200 p-16 text-center">
            <p className="text-slate-500">No submissions yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filtered.map((s) => (
              <article
                key={s.id}
                className="bg-white border border-slate-200 p-7 flex flex-col gap-4 hover:border-slate-900 transition-colors"
                data-testid={`submission-${s.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-display text-xl font-semibold text-slate-900">{s.name}</div>
                    <div className="mt-1 text-xs font-mono tracking-widest text-slate-500">
                      {new Date(s.created_at).toLocaleString()}
                    </div>
                  </div>
                  {s.interest && (
                    <span className="inline-flex items-center gap-2 text-xs px-3 py-1 border border-emerald-500/40 bg-emerald-50 text-emerald-700 font-medium">
                      <Tag className="w-3 h-3" strokeWidth={1.75} />
                      {INTEREST_LABELS[s.interest] || s.interest}
                    </span>
                  )}
                </div>
                <div className="space-y-1.5 text-sm">
                  <a href={`mailto:${s.email}`} className="flex items-center gap-2 text-slate-700 hover:text-emerald-600">
                    <Mail className="w-4 h-4 text-slate-400" strokeWidth={1.5} /> {s.email}
                  </a>
                  {s.phone && (
                    <a href={`tel:${s.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-emerald-600">
                      <Phone className="w-4 h-4 text-slate-400" strokeWidth={1.5} /> {s.phone}
                    </a>
                  )}
                  {s.company && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <Building2 className="w-4 h-4 text-slate-400" strokeWidth={1.5} /> {s.company}
                    </div>
                  )}
                </div>
                <div className="bg-slate-50 border-l-2 border-emerald-500 p-4 text-slate-700 text-sm whitespace-pre-wrap">
                  {s.message}
                </div>
                <div className="mt-auto pt-3 border-t border-slate-200 flex items-center justify-between gap-2">
                  <a
                    href={`mailto:${s.email}?subject=Re: Your inquiry to Cutting Edge Technologies&body=Hi ${s.name},%0D%0A%0D%0AThank you for reaching out.`}
                    className="inline-flex items-center gap-2 text-sm font-medium text-slate-900 hover:text-emerald-600"
                    data-testid={`reply-${s.id}`}
                  >
                    Reply by email <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </a>
                  <button
                    onClick={() => onDelete(s.id)}
                    className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-red-600"
                    data-testid={`delete-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
